<template>
  <div id="layout-wrapper" :class="{ 'nav-collapsed': isNavCollapsed }">
    <NavigationBar @nav-toggle="handleNavToggle" />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Leave Balance</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Leave Balance</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading leave balance...</p>
          </div>

          <div v-else-if="balanceData">
            <!-- Employee Info & Year Selector -->
            <div class="row">
              <div class="col-12">
                <div class="card bg-primary bg-gradient text-white">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <h5 class="text-white mb-2">{{ balanceData.employee.fullName }}</h5>
                        <p class="text-white-50 mb-0">
                          <i class="mdi mdi-badge-account me-1"></i> {{ balanceData.employee.empNo }}
                          <span class="mx-2">|</span>
                          <i class="mdi mdi-office-building me-1"></i> {{ balanceData.employee.department }}
                        </p>
                      </div>
                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <label class="text-white-50 small d-block">Select Year</label>
                        <select v-model="selectedYear" @change="loadBalanceData" class="form-select form-select-sm">
                          <option :value="currentYear">{{ currentYear }}</option>
                          <option :value="currentYear - 1">{{ currentYear - 1 }}</option>
                          <option :value="currentYear - 2">{{ currentYear - 2 }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Request Summary -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card border-0 shadow-sm">
                  <div class="card-header bg-transparent border-0 px-4 pt-4">
                    <h5 class="card-title mb-0 fs-5 text-dark fw-bold">
                      <i class="mdi mdi-chart-bar text-primary me-2"></i>
                      Request Summary
                    </h5>
                  </div>
                  <div class="card-body px-4 pb-4">
                    <div class="row g-3">
                      <div class="col-md-3 col-6">
                        <div class="text-center p-3 border rounded-3 bg-light">
                          <h3 class="text-primary mb-1 fw-bold">{{ balanceData.requestsSummary.total }}</h3>
                          <p class="text-muted mb-0 small">Total Requests</p>
                        </div>
                      </div>
                      <div class="col-md-3 col-6">
                        <div class="text-center p-3 border rounded-3 bg-light">
                          <h3 class="text-success mb-1 fw-bold">{{ balanceData.requestsSummary.approved }}</h3>
                          <p class="text-muted mb-0 small">Approved</p>
                          <small class="text-success fw-semibold">{{ balanceData.requestsSummary.totalDaysApproved }} days</small>
                        </div>
                      </div>
                      <div class="col-md-3 col-6">
                        <div class="text-center p-3 border rounded-3 bg-light">
                          <h3 class="text-warning mb-1 fw-bold">{{ balanceData.requestsSummary.pending }}</h3>
                          <p class="text-muted mb-0 small">Pending</p>
                          <small class="text-warning fw-semibold">{{ balanceData.requestsSummary.totalDaysPending }} days</small>
                        </div>
                      </div>
                      <div class="col-md-3 col-6">
                        <div class="text-center p-3 border rounded-3 bg-light">
                          <h3 class="text-danger mb-1 fw-bold">{{ balanceData.requestsSummary.rejected }}</h3>
                          <p class="text-muted mb-0 small">Rejected</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Detailed Balance Breakdown -->
            <div class="row">
              <div class="col-12">
                <div class="card border-0 bg-transparent mb-4">
                  <div class="card-header bg-transparent border-0 px-0 d-flex justify-content-between align-items-center mb-2">
                    <h5 class="card-title mb-0 fs-5 text-dark fw-bold">
                      <i class="mdi mdi-format-list-bulleted text-primary me-2"></i>
                      Leave Balances by Leave Type
                    </h5>
                    <button @click="gotTo('employee-leave-apply')" class="btn btn-primary px-3 shadow-sm rounded-pill">
                      <i class="mdi mdi-plus me-1"></i>
                      Apply for Leave
                    </button>
                  </div>
                  
                  <div v-if="balanceData.balances.length === 0" class="card card-body text-center py-5 shadow-sm border-0">
                    <i class="mdi mdi-information-outline text-muted font-size-48"></i>
                    <p class="text-muted mt-2">No leave balance information available</p>
                  </div>
                  
                  <div v-else class="row g-3">
                    <div v-for="balance in balanceData.balances" :key="balance.leaveTypeID" class="col-lg-4 col-md-6">
                      <div class="card leave-type-card border-0 shadow-sm h-100 overflow-hidden">
                        <div class="card-accent" :class="getAccentClass(balance.leaveTypeName)"></div>
                        <div class="card-body p-4 d-flex flex-column justify-content-between">
                          <div>
                            <div class="d-flex align-items-center justify-content-between mb-3">
                              <div class="d-flex align-items-center gap-2">
                                <span class="leave-icon-wrapper" :class="getIconColorClass(balance.leaveTypeName)">
                                  <i :class="getLeaveIcon(balance.leaveTypeName)"></i>
                                </span>
                                <div>
                                  <h6 class="mb-0 text-dark fw-bold">{{ balance.leaveTypeName }}</h6>
                                  <small class="text-muted text-truncate d-inline-block" style="max-width: 140px;" :title="balance.description">{{ balance.description || 'Employee leave category' }}</small>
                                </div>
                              </div>
                              <span class="badge rounded-pill" :class="getRemainingBadgeClass(balance.remainingDays)">
                                {{ getRemainingLabel(balance.remainingDays) }}
                              </span>
                            </div>
                            
                            <div class="row g-2 text-center my-3 bg-light rounded-3 p-3">
                              <div class="col-4">
                                <span class="d-block text-muted small text-uppercase" style="font-size: 10px;">Allocated</span>
                                <strong class="fs-5 text-secondary">{{ balance.totalDays }}</strong>
                              </div>
                              <div class="col-4 border-start border-end">
                                <span class="d-block text-muted small text-uppercase" style="font-size: 10px;">Used</span>
                                <strong class="fs-5 text-warning">{{ balance.usedDays }}</strong>
                              </div>
                              <div class="col-4">
                                <span class="d-block text-muted small text-uppercase" style="font-size: 10px;">Remaining</span>
                                <strong class="fs-5 text-success">{{ balance.remainingDays }}</strong>
                              </div>
                            </div>
                          </div>
                          
                          <div class="mt-2">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                              <span class="small text-muted" style="font-size: 11px;">Usage Rate</span>
                              <span class="small fw-semibold" :class="getUsageColorText(balance.percentageUsed)" style="font-size: 11px;">{{ balance.percentageUsed }}%</span>
                            </div>
                            <div class="progress" style="height: 8px; border-radius: 4px;">
                              <div
                                  class="progress-bar"
                                  :class="getProgressBarClass(balance.percentageUsed)"
                                  :style="{ width: balance.percentageUsed + '%' }">
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Upcoming Leaves -->
            <div class="row" v-if="balanceData.upcomingLeaves.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-calendar-clock text-primary me-2"></i>
                      Upcoming Approved Leaves
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead class="table-light">
                        <tr>
                          <th>Leave Type</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th class="text-center">Days</th>
                          <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="leave in balanceData.upcomingLeaves" :key="leave.id">
                          <td>{{ leave.leaveTypeName }}</td>
                          <td>{{ formatDate(leave.startDate) }}</td>
                          <td>{{ formatDate(leave.endDate) }}</td>
                          <td class="text-center">
                            <span class="badge bg-info">{{ leave.totalDays }} days</span>
                          </td>
                          <td>
                              <span class="badge bg-success">
                                <i class="mdi mdi-check-circle me-1"></i>Approved
                              </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">Need to apply for leave?</h6>
                        <p class="text-muted mb-0">Check your balance and submit a request</p>
                      </div>
                      <div class="d-flex gap-2">
                        <button @click="gotTo('employee-leave-apply')" class="btn btn-primary">
                          <i class="mdi mdi-plus me-1"></i>
                          Apply for Leave
                        </button>
                        <button @click="gotTo('employee-leave-history')" class="btn btn-outline-primary">
                          <i class="mdi mdi-history me-1"></i>
                          View History
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeLeaveBalance",
  components: { NavigationBar },

  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      balanceData: null,
      currentYear: new Date().getFullYear(),
      selectedYear: new Date().getFullYear()
    };
  },

  mounted() {
    this.loadBalanceData();
  },

  methods: {
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    async loadBalanceData() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeLeaveBalanceDetailed");
        data.append("year", this.selectedYear);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.balanceData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load leave balance');
        }
      } catch (error) {
        this.$toast?.error("Failed to load leave balance: " + error.message) ||
        console.error("Failed to load leave balance:", error);
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    getProgressBarClass(percentage) {
      if (percentage >= 80) return 'bg-danger';
      if (percentage >= 50) return 'bg-warning';
      return 'bg-success';
    },

    getAccentClass(name) {
      const lower = String(name).toLowerCase();
      if (lower.includes('annual')) return 'accent-annual';
      if (lower.includes('sick')) return 'accent-sick';
      if (lower.includes('parental') || lower.includes('maternity') || lower.includes('paternity')) return 'accent-parental';
      return 'accent-other';
    },

    getLeaveIcon(name) {
      const lower = String(name).toLowerCase();
      if (lower.includes('annual')) return 'mdi mdi-palm-tree';
      if (lower.includes('sick') || lower.includes('medical')) return 'mdi mdi-heart-pulse';
      if (lower.includes('parental') || lower.includes('maternity') || lower.includes('paternity')) return 'mdi mdi-baby-carriage';
      if (lower.includes('compassionate')) return 'mdi mdi-hand-heart';
      if (lower.includes('study') || lower.includes('education')) return 'mdi mdi-book-open-page-variant';
      return 'mdi mdi-calendar-star';
    },

    getIconColorClass(name) {
      const lower = String(name).toLowerCase();
      if (lower.includes('annual')) return 'icon-annual';
      if (lower.includes('sick') || lower.includes('medical')) return 'icon-sick';
      if (lower.includes('parental') || lower.includes('maternity') || lower.includes('paternity')) return 'icon-parental';
      return 'icon-other';
    },

    getRemainingBadgeClass(remaining) {
      if (remaining > 5) return 'bg-success-subtle text-success';
      if (remaining > 0) return 'bg-warning-subtle text-warning';
      return 'bg-danger-subtle text-danger';
    },

    getRemainingLabel(remaining) {
      if (remaining > 5) return 'Good';
      if (remaining > 0) return 'Low';
      return 'Depleted';
    },

    getUsageColorText(percentage) {
      if (percentage >= 80) return 'text-danger';
      if (percentage >= 50) return 'text-warning';
      return 'text-success';
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
#layout-wrapper {
  min-height: 100vh;
  box-sizing: border-box;
}

.content-minimize {
  margin-left: 280px;
  padding: 20px;
  min-height: 100vh;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

#layout-wrapper.nav-collapsed .content-minimize {
  margin-left: 70px;
}

@media (max-width: 1024px) {
  .content-minimize {
    margin-left: 70px;
  }
}

@media (max-width: 768px) {
  .content-minimize {
    margin-left: 0;
    padding: 15px;
  }
  #layout-wrapper.nav-collapsed .content-minimize {
    margin-left: 0;
  }
}

.card-stats {
  transition: all 0.3s ease;
}

.card-stats:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.donut-chart {
  display: flex;
  justify-content: center;
  align-items: center;
}

.legend-box {
  width: 12px;
  height: 12px;
  display: inline-block;
  border-radius: 2px;
}

.progress {
  background-color: #e9ecef;
}

.table thead th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.table tbody tr:hover {
  background-color: rgba(91, 115, 232, 0.05);
}

.leave-type-card {
  position: relative;
  border-radius: 12px;
  background-color: #fff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
}

.leave-type-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
}

.card-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  border-radius: 12px 12px 0 0;
}

.accent-annual {
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

.accent-sick {
  background: linear-gradient(90deg, #ef4444, #f87171);
}

.accent-parental {
  background: linear-gradient(90deg, #ec4899, #f472b6);
}

.accent-other {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.leave-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.icon-annual {
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.icon-sick {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.icon-parental {
  background-color: rgba(236, 72, 153, 0.1);
  color: #ec4899;
}

.icon-other {
  background-color: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.bg-success-subtle {
  background-color: rgba(16, 185, 129, 0.1) !important;
  color: #10b981 !important;
}

.bg-warning-subtle {
  background-color: rgba(245, 158, 11, 0.1) !important;
  color: #f59e0b !important;
}

.bg-danger-subtle {
  background-color: rgba(239, 68, 68, 0.1) !important;
  color: #ef4444 !important;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>